import { useInventoryStore } from '@/stores/InventoryStore/InventoryStore';
import { backOfficeMessages } from '@/views/gallery/back-office-messages';
import { getRoute, RoutePaths } from '@/views/gallery/route-paths';
import { NeonAlertService } from '@art-basel/artbasel-lib-neon';
import {
  type AcceptPurchaseRequestPayload,
  type ArcualAccountProfile,
  type ArcualUserContext,
  getAccountService,
  getPurchaseRequestsService,
  type HttpError,
  type PurchaseRequest,
  type PurchaseRequestBaseSseEventPayload,
  PurchaseRequestEvent,
  type PurchaseRequestEventType,
  type PurchaseRequestsBaseSseEventPayload,
  type PurchaseRequestsData,
  type PurchaseRequestsSearchParams,
  type PurchaseRequestSseEventPayload,
  type PurchaseRequestsService,
  type PurchaseRequestSummary,
} from '@art-basel/artbasel-lib-platform';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAmplitudeFeatureFlags } from '@/composables/useAmplitudeFeatureFlags';

const { featureFlags } = useAmplitudeFeatureFlags();

const PurchaseRequestsStoreName = 'PurchaseRequestsStore';
export const usePurchaseRequestsStore = defineStore(PurchaseRequestsStoreName, () => {
  // store state
  const purchaseRequestsList = ref<PurchaseRequestSummary[]>([]);
  const purchaseRequestDetailsList = ref<PurchaseRequest[]>([]);

  // internal state
  const purchaseRequestsService: PurchaseRequestsService = getPurchaseRequestsService();
  const lastPurchaseRequestsListCall = ref<() => Promise<PurchaseRequestSummary[]>>(); // todo maybe expose this to the outside?
  const isRefreshNotificationOpen = ref(false);
  const router = useRouter();

  const getPurchaseRequestById = computed(() => {
    return (id: string) => {
      return purchaseRequestDetailsList.value.find((purchaseRequest) => purchaseRequest.id === id);
    };
  });

  const onPurchaseRequestCollectorUpdatedEvent = (
    purchaseRequestSseEventPayload: PurchaseRequestBaseSseEventPayload | PurchaseRequestsBaseSseEventPayload,
    account: ArcualAccountProfile,
  ) => {
    const data: PurchaseRequestsData = purchaseRequestSseEventPayload.data as PurchaseRequestsData;
    const galleryPurchaseRequests: Array<PurchaseRequestEventType> = data.offers;
    // filter if galleryProfileId is different
    const filteredGalleryPurchaseRequests: Array<PurchaseRequestEventType> = galleryPurchaseRequests.filter(
      (purchaseRequest) => purchaseRequest.galleryProfileId === account.id,
    );
    if (filteredGalleryPurchaseRequests.length > 0) {
      purchaseRequestUpdatedNotification();
    }
  };

  const setupPurchaseRequestsSseListener = async () => {
    if (!featureFlags.value.isSsePurchaseRequestOffersEnabled) {
      return;
    }
    return purchaseRequestsService.addSseConnection(
      'offers',
      async (purchaseRequestSseEventPayload: PurchaseRequestSseEventPayload) => {
        // bail out if event is keep-alive
        if (purchaseRequestSseEventPayload.type === PurchaseRequestEvent.PURCHASE_REQUEST_KEEP_ALIVE) {
          return;
        }

        const account = await getAccountService().getAccountProfile();

        const store = usePurchaseRequestsStore();
        switch (purchaseRequestSseEventPayload.type) {
          case PurchaseRequestEvent.PURCHASE_REQUEST_TERMINATED:
            // bail out if galleryProfileId is different
            if (purchaseRequestSseEventPayload.data.galleryProfileId !== account.id) {
              return;
            }
            store.purchaseRequestTerminated(
              await getPurchaseRequestsService().getPurchaseRequestById(
                (purchaseRequestSseEventPayload.data as PurchaseRequestEventType).id,
              ),
            );
            useInventoryStore().invalidateItem(
              (purchaseRequestSseEventPayload.data as PurchaseRequestEventType).artwork.id,
            );
            break;
          case PurchaseRequestEvent.PURCHASE_REQUEST_CREATED:
          case PurchaseRequestEvent.PURCHASE_REQUEST_UPDATED:
          case PurchaseRequestEvent.PURCHASE_REQUEST_WITHDRAWN:
          case PurchaseRequestEvent.PURCHASE_REQUEST_PAID:
            // bail out if galleryProfileId is different
            if (purchaseRequestSseEventPayload.data.galleryProfileId !== account.id) {
              return;
            }

            store.purchaseRequestUpdated(
              await getPurchaseRequestsService().getPurchaseRequestById(
                (purchaseRequestSseEventPayload.data as PurchaseRequestEventType).id,
              ),
            );
            purchaseRequestUpdatedNotification();
            break;
          case PurchaseRequestEvent.PURCHASE_REQUEST_COLLECTOR_UPDATED:
            onPurchaseRequestCollectorUpdatedEvent(purchaseRequestSseEventPayload, account);
            break;
        }
      },
    );
  };
  const purchaseRequestUpdatedNotification = () => {
    const store = usePurchaseRequestsStore();
    if (!isRefreshNotificationOpen.value) {
      isRefreshNotificationOpen.value = true;
      NeonAlertService.info({
        title: backOfficeMessages.updatedPurchaseRequestsNotificationTitle,
        message: backOfficeMessages.updatedPurchaseRequestsNotificationDescription,
        dismissible: true,
        primaryAction: {
          label: backOfficeMessages.updatedPurchaseRequestsNotificationPrimaryAction,
          callback: async () => {
            isRefreshNotificationOpen.value = false;
            await store.refreshPurchaseRequestsList();
            await router.push(getRoute(RoutePaths.SELL.REQUESTS));
          },
        },
        secondaryAction: {
          label: backOfficeMessages.updatedPurchaseRequestsNotificationSecondaryAction,
          callback: () => {
            isRefreshNotificationOpen.value = false;
          },
        },
      });
    }
  };

  /**
   * updates the state after an offer has been terminated async,
   * enables us to listen for this with actions
   *
   * @param purchaseRequest
   */
  const purchaseRequestTerminated = (purchaseRequest: PurchaseRequest) => purchaseRequestUpdated(purchaseRequest);

  const purchaseRequestUpdated = (updatedPurchaseRequest: PurchaseRequest) => {
    purchaseRequestDetailsList.value = [
      ...purchaseRequestDetailsList.value.filter((purchaseRequest) => purchaseRequest.id !== updatedPurchaseRequest.id),
      updatedPurchaseRequest,
    ];
    return updatedPurchaseRequest;
  };

  const getPurchaseRequestsByArtworkId = async (artworkId: string) => {
    try {
      return await getPurchaseRequestsService().getPurchaseRequestsByArtworkId(artworkId);
    } catch (e) {
      handleError(e as HttpError);
      return Promise.reject(e);
    }
  };

  const getPurchaseRequests = async (context: ArcualUserContext) => {
    try {
      const purchaseRequests = await getPurchaseRequestsService().getPurchaseRequests(context);
      storeLastPurchaseRequestsListCall(() => {
        return getPurchaseRequestsService().getPurchaseRequests(context);
      });
      updatePurchaseRequestsList(purchaseRequests);
      return purchaseRequestsList;
    } catch (e) {
      handleError(e as HttpError);
      return Promise.reject(e);
    }
  };

  const storeLastPurchaseRequestsListCall = (call: () => Promise<PurchaseRequestSummary[]>) => {
    lastPurchaseRequestsListCall.value = call;
  };

  const updatePurchaseRequestsList = (purchaseRequests: PurchaseRequestSummary[]) => {
    purchaseRequestsList.value = purchaseRequests.map((purchaseRequest) => purchaseRequest);
  };

  const handleError = (error: HttpError) => {
    console.error(error);
    // TODO: integrate with sentry https://app.clickup.com/t/14392315/APL-2953
  };

  const loadPurchaseRequestById = async (id: string) => {
    try {
      const galleryPurchaseRequest = await getPurchaseRequestsService().getPurchaseRequestById(id);
      const store = usePurchaseRequestsStore();
      return store.purchaseRequestUpdated(galleryPurchaseRequest);
    } catch (e) {
      handleError(e as HttpError);
      return Promise.reject(e);
    }
  };

  const searchPurchaseRequests = async (searchParams: PurchaseRequestsSearchParams, context: ArcualUserContext) => {
    try {
      const purchaseRequests = await getPurchaseRequestsService().searchPurchaseRequests(searchParams, context);
      storeLastPurchaseRequestsListCall(() => {
        return getPurchaseRequestsService().searchPurchaseRequests(searchParams, context);
      });
      updatePurchaseRequestsList(purchaseRequests);
      return purchaseRequestsList;
    } catch (e) {
      handleError(e as HttpError);
      return Promise.reject(e);
    }
  };

  const refreshPurchaseRequestsList = async () => {
    try {
      if (lastPurchaseRequestsListCall.value !== undefined) {
        const purchaseRequests = await lastPurchaseRequestsListCall.value();
        updatePurchaseRequestsList(purchaseRequests);
      }
      return purchaseRequestsList;
    } catch (e) {
      handleError(e as HttpError);
      return Promise.reject(e);
    }
  };

  const acceptPurchaseRequest = async (
    purchaseRequest: PurchaseRequest,
    acceptPurchaseRequestPayload: AcceptPurchaseRequestPayload,
  ): Promise<PurchaseRequest> =>
    new Promise((resolve, reject) => {
      getPurchaseRequestsService()
        .acceptPurchaseRequest(purchaseRequest.id, acceptPurchaseRequestPayload)
        .then(async () => {
          /// todo this is temporary until the BE returns the entity properly
          getPurchaseRequestsService()
            .getPurchaseRequestById(purchaseRequest.id)
            .then((updatedPurchaseRequest) => {
              resolve(purchaseRequestUpdated(updatedPurchaseRequest));
            })
            .catch(handleError);
        })
        .catch((e) => {
          handleError(e);
          reject(e);
        });
    });

  const terminatePurchaseRequest = async (purchaseRequest: PurchaseRequest) =>
    new Promise<void>((resolve, reject) => {
      getPurchaseRequestsService()
        .terminatePurchaseRequest(purchaseRequest)
        .then(async () => {
          resolve();
        })
        .catch((e) => {
          handleError(e);
          reject(e);
        });
    });

  /**
   * Reset the store to its initial state
   */
  const reset = () => {
    purchaseRequestsList.value = [];
    purchaseRequestDetailsList.value = [];
    lastPurchaseRequestsListCall.value = undefined;
    isRefreshNotificationOpen.value = false;
  };

  return {
    purchaseRequestsList,
    purchaseRequestDetailsList,
    getPurchaseRequestById,
    setupPurchaseRequestsSseListener,
    getPurchaseRequestsByArtworkId,
    getPurchaseRequests,
    loadPurchaseRequestById,
    searchPurchaseRequests,
    refreshPurchaseRequestsList,
    acceptPurchaseRequest,
    terminatePurchaseRequest,
    purchaseRequestTerminated,
    purchaseRequestUpdated,
    purchaseRequestUpdatedNotification,
    storeLastPurchaseRequestsListCall,
    updatePurchaseRequestsList,
    handleError,
    reset,
  };
});
